import Image from 'next/image';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import { generateArticleSchema } from '../../utils/schemaGenerators';

export const metadata = {
  title: 'Modern Build Featured in VoyageLA: Kitchen, Bath, and Home Remodeling',
  image: '/blog/voyagela-feature-modern-build-home-remodeling.webp',
  date: 'October 6, 2025',
  author: 'Modern Build Team',
  excerpt:
    'VoyageLA features the story of Modern Build Home Remodeling and its founder, Matan Bodek, exploring how technology, AI, and family-driven values are redefining the home renovation experience across Southern California.',
  category: 'Company News',
  tags: ['VoyageLA', 'Modern Build', 'Home Remodeling', 'AI', 'Leadership', 'Innovation'],
};

export default function VoyageLAFeature() {
  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="title" content={metadata.title} />
        <meta name="description" content={metadata.excerpt} />
        <meta name="author" content={metadata.author} />
        <meta name="keywords" content={metadata.tags.join(', ')} />
        <link
          rel="canonical"
          href={`https://modernbuildremodel.com/blog/voyagela-feature-modern-build-home-remodeling`}
        />

        <meta property="og:title" content={metadata.title} />
        <meta property="og:description" content={metadata.excerpt} />
        <meta property="og:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta property="og:url" content={`https://modernbuildremodel.com/blog/voyagela-feature-modern-build-home-remodeling`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metadata.title} />
        <meta name="twitter:description" content={metadata.excerpt} />
        <meta name="twitter:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta name="twitter:site" content="@modernbuildhome" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://modernbuildremodel.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": `https://modernbuildremodel.com/blog`
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "VoyageLA Feature: Modern Build",
                  "item": `https://modernbuildremodel.com/blog/voyagela-feature-modern-build-home-remodeling`
                }
              ]
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateArticleSchema(metadata, 'voyagela-feature-modern-build-home-remodeling')),
          }}
        />
      </Head>

      <Header />

      <div className="bg-white">
        <Hero category="voyageLAFeature" page="page" hasContentH1={false}/>

        <div className="container mx-auto px-4 py-10 max-w-4xl">
          <p className="text-sm text-black mb-2">
            <span className="capitalize text-primary tracking-[3px]">
              By {metadata.author} / {metadata.date}
            </span>
          </p>

          <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] mb-10 rounded-lg overflow-hidden">
            <Image
              src={metadata.image}
              alt={`VoyageLA feature: ${metadata.title}`}
              title={metadata.title}
              fill
              sizes="(max-width: 768px) 100vw, 700px"
              className="object-cover rounded-lg"
            />
          </div>

          <div className="prose prose-lg max-w-none text-md text-black leading-relaxed space-y-6">

            <p><strong>Modern Build Home Remodeling</strong> was recently featured in <a target="_blank" href="https://voyagela.com/interview/inspiring-conversations-with-matan-bodek-of-modern-build-home-remodeling" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold underline">VoyageLA</a>, sharing the inspiring story behind the company and its founder, <strong>Matan Bodek</strong>.</p>

            <p>The feature dives into how Modern Build bridges the worlds of <strong>technology, product management, and home remodeling</strong>. Founded by Matan, a seasoned product leader and software developer, Modern Build was created to bring transparency, innovation, and human connection to the remodeling industry.</p>

            <p>Unlike traditional remodeling firms, Modern Build integrates modern technologies such as <strong>AI, data-driven tools, and project visualization systems</strong> to improve communication, streamline processes, and help clients envision their dream spaces before construction even begins. Yet, while embracing innovation, the company remains rooted in <strong>family-driven values and personalized service</strong>.</p>

            <h3 className="font-bold">Merging Technology with Craftsmanship</h3>
            <p>Modern Build’s approach combines the precision of software development with the creativity of construction. From <strong>project tracking and data insights</strong> to visual concept generation, every tool serves one goal—to make the client journey smoother, more informed, and deeply personal.</p>

            <p>But technology never replaces the human touch. As Matan shared in the VoyageLA interview, <em>“Technology should enhance connection, not replace it.”</em> Every project at Modern Build is designed around care, transparency, and trust—values that turn renovation into a collaborative experience.</p>

            <h3 className="font-bold">From Product Management to Home Innovation</h3>
            <p>Matan’s background in <strong>product management, app development, and AI leadership</strong> shaped Modern Build’s foundation. Each principle—efficiency, iteration, and user-centric design—is now applied to construction, redefining what a remodeling company can be in the modern age.</p>

            <p>Through this blend of <strong>strategy, marketing vision, and innovation</strong>, Modern Build aims to raise the standard for home renovation across Southern California, creating experiences that are both cutting-edge and deeply human.</p>

            <p>“Every stage of my career taught me something about leadership, vision, and building from the ground up,” says Matan. “Modern Build is where all those lessons come together.”</p>

            <p className="mt-8"><strong>We thank VoyageLA</strong> for the feature and for helping share our mission to bring innovation and authenticity to home remodeling. To read the full story, visit the official VoyageLA interview below.</p>

            <p>
              🔗 <a target="_blank" href="https://voyagela.com/interview/inspiring-conversations-with-matan-bodek-of-modern-build-home-remodeling" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold underline">Read the full article on VoyageLA</a>
            </p>

          </div>
        </div>

        <SocialProof />
        <ContactUs />
        <Footer />
      </div>
    </>
  );
}
